import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import EventTypeBadge from './EventTypeBadge';
import EventApprovalStatusBadge from './EventApprovalStatusBadge';
import EventPublishedSwitch from './EventPublishedSwitch';
import EventRowActions from './EventRowActions';
import {
  canSubmitPartnerEvent,
  canShowPartnerEventEdit,
} from '../../../contexts/EventsScopeContext';
import {
  canArchivePartnerEvent,
  formatPartnerEventArchivedAt,
  isPartnerEventArchived,
} from '../../Partner/Events/partnerEventArchive';

const COVER_HEIGHT = 160;

const EventCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const CoverWrap = styled(Box)({
  position: 'relative',
  height: COVER_HEIGHT,
  backgroundColor: '#000',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const CoverFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isArchived',
})(({ isArchived }) => ({
  width: COVER_HEIGHT,
  height: COVER_HEIGHT,
  flexShrink: 0,
  overflow: 'hidden',
  ...(isArchived
    ? {
        filter: 'grayscale(1)',
        opacity: 0.72,
      }
    : {}),
}));

const CoverImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const PlaceholderCover = styled(Box)({
  width: '100%',
  height: '100%',
  backgroundColor: '#1a1a2e',
});

const CardBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
}));

const CardFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const MetaRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
  width: '100%',
  overflow: 'hidden',
}));

const metaTextSx = {
  fontSize: '0.8125rem',
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const MetaItem = ({ icon, children, sx = {} }) => {
  const label = typeof children === 'string' ? children : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {icon}
      <Typography variant="caption" color="text.secondary" sx={metaTextSx} title={label}>
        {children}
      </Typography>
    </Box>
  );
};

const iconSx = { fontSize: 18, color: 'text.secondary', flexShrink: 0 };

const EventsGridView = ({
  events,
  onEventClick,
  onView,
  onEdit,
  onDelete,
  onArchive,
  onPublishedChange,
  togglingPublishedId = null,
  isPublishDisabled = () => false,
  onSubmit,
  submittingEventId = null,
  features = null,
  viewOnlyRowActions = false,
}) => (
  <Grid container spacing={2.5}>
    {events.map((event) => {
      const coverSrc = event.coverImageUrl || event.imageUrl;
      const dateTimeDisplay = event.dateTime || event.dateLine || '—';
      const locationDisplay = event.location || event.locationLine || '—';
      const seatsDisplay = `${event.seatsAvailable ?? '—'}/${event.totalSeats ?? '—'}`;
      const isPartnerGrid = Boolean(features?.requiresSaveAs);
      const editHandler = viewOnlyRowActions
        ? undefined
        : onEdit && (!isPartnerGrid || canShowPartnerEventEdit(event, features))
          ? onEdit
          : undefined;
      const archived = isPartnerGrid && isPartnerEventArchived(event);
      const showArchive = !viewOnlyRowActions && isPartnerGrid && canArchivePartnerEvent(event);
      const archivedLabel = formatPartnerEventArchivedAt(event.deletedAt);

      return (
        <Grid item xs={12} sm={6} key={event.id}>
          <EventCard
            onClick={() => onEventClick?.(event.id)}
            sx={{
              cursor: onEventClick ? 'pointer' : 'default',
              transition: 'box-shadow 0.2s',
              opacity: archived ? 0.88 : 1,
              bgcolor: archived ? 'action.hover' : undefined,
              '&:hover': onEventClick ? { boxShadow: '0 4px 16px rgba(0,0,0,0.08)' } : undefined,
            }}
          >
            <CoverWrap>
              <CoverFrame isArchived={archived}>
                {coverSrc ? (
                  <CoverImage src={coverSrc} alt="" loading="lazy" />
                ) : (
                  <PlaceholderCover />
                )}
              </CoverFrame>
              {isPartnerGrid ? (
                <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                  <EventApprovalStatusBadge status={event.approvalStatus} />
                </Box>
              ) : null}
            </CoverWrap>
            <CardBody>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mb: 1.25,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1rem',
                    lineHeight: 1.35,
                    color: 'text.primary',
                  }}
                >
                  {event.title}
                </Typography>
                <EventTypeBadge type={event.type} compact />
              </Box>

              {archived && archivedLabel ? (
                <Typography
                  variant="caption"
                  sx={{ color: '#BF360C', fontWeight: 600, display: 'block', mb: 0.5 }}
                >
                  Archived {archivedLabel}
                </Typography>
              ) : null}
              {event.wingmanDescription ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 0.5,
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {event.wingmanDescription}
                </Typography>
              ) : null}

              <MetaRow>
                <MetaItem
                  icon={<CalendarTodayOutlinedIcon sx={iconSx} />}
                  sx={{ flex: '0 1 auto', maxWidth: '42%' }}
                >
                  {dateTimeDisplay}
                </MetaItem>
                <MetaItem
                  icon={<PlaceOutlinedIcon sx={iconSx} />}
                  sx={{ flex: '1 1 0', minWidth: 0 }}
                >
                  {locationDisplay}
                </MetaItem>
                <MetaItem icon={<GroupsOutlinedIcon sx={iconSx} />} sx={{ flexShrink: 0 }}>
                  {seatsDisplay}
                </MetaItem>
              </MetaRow>

              <CardFooter
                onClick={(e) => e.stopPropagation()}
                sx={{ justifyContent: isPartnerGrid ? 'flex-end' : 'space-between' }}
              >
                {onPublishedChange ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Published
                    </Typography>
                    <EventPublishedSwitch
                      checked={event.published}
                      disabled={togglingPublishedId === event.id || isPublishDisabled(event)}
                      onChange={(e) => onPublishedChange(event.id, e.target.checked)}
                      inputProps={{ 'aria-label': `Toggle published for ${event.title}` }}
                    />
                  </Box>
                ) : !viewOnlyRowActions && !archived && onSubmit && canSubmitPartnerEvent(event) ? (
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    loading={submittingEventId === event.id}
                    onClick={() => onSubmit(event.id)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1,
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    Submit
                  </LoadingButton>
                ) : null}
                <EventRowActions
                  eventId={event.id}
                  onView={onView}
                  onEdit={editHandler}
                  onArchive={showArchive && onArchive ? onArchive : undefined}
                  onDelete={!onArchive ? onDelete : undefined}
                />
              </CardFooter>
            </CardBody>
          </EventCard>
        </Grid>
      );
    })}
  </Grid>
);

export default EventsGridView;
